import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Folder, 
  FolderOpen, 
  Plus, 
  Trash2, 
  MoreVertical,
  ChevronRight,
  ChevronDown,
  FolderTree
} from 'lucide-react';
import { Matter } from '../../types';
import { useDocuments } from '../../hooks/useDocuments';

/**
 * Folder tree item for hierarchical navigation
 */
interface FolderTreeItem {
  id: string;
  name: string;
  path: string;
  parentPath: string;
  level: number;
  isExpanded: boolean;
  hasChildren: boolean;
  createdDateTime: string;
  lastModifiedDateTime: string;
}

/**
 * Props for DocumentTree component
 */
interface DocumentTreeProps {
  matter: Matter;
  currentPath: string;
  onFolderSelect: (path: string) => void;
  onFolderCreate?: (name: string, parentPath: string) => void;
  onFolderDelete?: (folderId: string) => void;
  className?: string;
}

/**
 * Context menu for folder operations
 */
interface ContextMenuProps {
  position: { x: number; y: number };
  onClose: () => void;
  onCreateFolder: () => void;
  onDeleteFolder: () => void;
}

const FolderContextMenu: React.FC<ContextMenuProps> = ({
  position,
  onClose,
  onCreateFolder,
  onDeleteFolder
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-gray-800 border border-gray-600 rounded-md shadow-lg py-1 min-w-[160px]"
      style={{ left: position.x, top: position.y }}
    >
      <button
        onClick={onCreateFolder}
        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
      >
        <Plus className="w-4 h-4" />
        New Folder
      </button>
      <button
        onClick={onDeleteFolder}
        className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
      >
        <Trash2 className="w-4 h-4" />
        Delete Folder
      </button>
    </div>
  );
};

/**
 * DocumentTree Component
 * Provides hierarchical folder navigation with expandable tree structure
 * Supports right-click context menu for folder operations
 * Maximum depth of 3 levels as per requirements
 */
const DocumentTree: React.FC<DocumentTreeProps> = ({
  matter,
  currentPath,
  onFolderSelect,
  onFolderCreate,
  onFolderDelete,
  className = ''
}) => {
  const { folders, loadFolders, createFolder, deleteFolder, isFolderLoading } = useDocuments();
  
  // UI state
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set([''])); // Root expanded by default
  const [contextMenu, setContextMenu] = useState<{
    folder: FolderTreeItem;
    position: { x: number; y: number };
  } | null>(null);
  const [newFolderInput, setNewFolderInput] = useState<{
    parentPath: string;
    isVisible: boolean;
    value: string;
  }>({ parentPath: '', isVisible: false, value: '' });

  // Load folders when component mounts or matter changes
  useEffect(() => {
    loadFolders(matter, '');
  }, [matter]); // Removed loadFolders from dependencies to prevent infinite loop

  // Add global click handler to close context menus
  useEffect(() => {
    const handleGlobalClick = () => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [contextMenu]);

  // Build tree structure from flat folder list
  const buildFolderTree = useCallback((): FolderTreeItem[] => {
    const pathMap = new Map<string, FolderTreeItem>();

    // First, create all folder items
    folders.forEach(folder => {
      const level = folder.path === '/' ? 0 : folder.path.split('/').filter(p => p !== '').length;
      const treeItem: FolderTreeItem = {
        id: folder.id,
        name: folder.name,
        path: folder.path,
        parentPath: folder.parentPath,
        level,
        isExpanded: expandedFolders.has(folder.path),
        hasChildren: false, // Will be calculated below
        createdDateTime: folder.createdDateTime,
        lastModifiedDateTime: folder.lastModifiedDateTime
      };
      
      pathMap.set(folder.path, treeItem);
    });

    // Calculate hasChildren for each folder
    folders.forEach(folder => {
      const item = pathMap.get(folder.path);
      if (!item) return;
      item.hasChildren = folders.some(f => f.parentPath === folder.path);
    });

    // Build hierarchical tree by recursively collecting folders
    const collectVisibleFolders = (parentPath: string, targetLevel: number): FolderTreeItem[] => {
      const childFolders = folders
        .filter(f => f.parentPath === parentPath)
        .map(f => pathMap.get(f.path))
        .filter((item): item is FolderTreeItem => item !== undefined)
        .sort((a, b) => a.name.localeCompare(b.name)); // Sort by name

      const result: FolderTreeItem[] = [];
      
      childFolders.forEach(folder => {
        result.push(folder);
        
        // If this folder is expanded, recursively add its children
        if (folder.isExpanded && folder.hasChildren) {
          const children = collectVisibleFolders(folder.path, targetLevel + 1);
          result.push(...children);
        }
      });
      
      return result;
    };

    // Start with root level folders (parentPath === '')
    return collectVisibleFolders('', 0);
  }, [folders, expandedFolders]);

  // Handle folder expansion/collapse
  const handleToggleExpand = useCallback((folder: FolderTreeItem, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const newExpanded = new Set(expandedFolders);
    
    if (folder.isExpanded) {
      newExpanded.delete(folder.path);
    } else {
      newExpanded.add(folder.path);
      // No need to load folders - we already have all folders loaded recursively
    }
    
    setExpandedFolders(newExpanded);
  }, [expandedFolders]);

  // Handle folder selection
  const handleFolderSelect = useCallback((folder: FolderTreeItem) => {
    onFolderSelect(folder.path);
  }, [onFolderSelect]);

  // Handle right-click context menu
  const handleContextMenu = useCallback((event: React.MouseEvent, folder: FolderTreeItem) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      folder,
      position: { x: event.clientX, y: event.clientY }
    });
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Handle folder creation from context menu
  const handleCreateFolderFromContext = useCallback(() => {
    if (contextMenu) {
      const parentPath = contextMenu.folder.path;
      console.log(`🔵 Creating subfolder in context menu - Parent folder: "${contextMenu.folder.name}", Parent path: "${parentPath}"`);
      
      setNewFolderInput({
        parentPath: parentPath,
        isVisible: true,
        value: ''
      });
      setContextMenu(null);
    }
  }, [contextMenu]);

  // Handle folder deletion from context menu with confirmation
  const handleDeleteFolderFromContext = useCallback(async () => {
    if (contextMenu) {
      const folder = contextMenu.folder;
      
      // Simple but effective warning
      const confirmMessage = `Are you sure you want to delete the folder "${folder.name}"?\n\n⚠️ WARNING: This will permanently delete the folder and ALL files and subfolders contained within it.\n\nThis action cannot be undone.`;
      
      if (window.confirm(confirmMessage)) {
        try {
          console.log(`🗑️ Deleting folder "${folder.name}" (ID: ${folder.id})`);
          await deleteFolder(matter, folder.id);
          
          if (onFolderDelete) {
            onFolderDelete(folder.id);
          }
          
          console.log('✅ Folder deleted successfully');
        } catch (error) {
          console.error('❌ Failed to delete folder:', error);
          alert(`Failed to delete folder: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
      setContextMenu(null);
    }
  }, [contextMenu, onFolderDelete, deleteFolder, matter]);

  // Handle new folder creation
  const handleCreateNewFolder = useCallback(async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && newFolderInput.value.trim()) {
      try {
        const folderName = newFolderInput.value.trim();
        await createFolder(matter, folderName, newFolderInput.parentPath);
        
        // Refresh folder list to show new folder
        console.log('🔄 Refreshing folder list after Enter key creation...');
        await loadFolders(matter, ''); // Refresh entire tree
        
        if (onFolderCreate) {
          onFolderCreate(folderName, newFolderInput.parentPath);
        }
        
        // Expand parent folder to show new folder and clear UI state
        setExpandedFolders(prev => new Set([...prev, newFolderInput.parentPath]));
        setNewFolderInput({ parentPath: '', isVisible: false, value: '' });
        setContextMenu(null); // Clear any open context menu
      } catch (error) {
        console.error('Failed to create folder:', error);
      }
    } else if (event.key === 'Escape') {
      setNewFolderInput({ parentPath: '', isVisible: false, value: '' });
    }
  }, [newFolderInput, createFolder, matter, onFolderCreate, loadFolders]);

  // Handle root folder selection
  const handleRootSelect = useCallback(() => {
    onFolderSelect('');
  }, [onFolderSelect]);

  const treeItems = buildFolderTree();

  return (
    <div className={`bg-gray-800 border border-gray-700 rounded-lg ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700 bg-gray-750">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <FolderTree className="w-4 h-4" />
            Folders
          </h3>
          {/* Create Folder Button - Always visible */}
          {!newFolderInput.isVisible && (
            <button
              onClick={() => setNewFolderInput({ parentPath: '', isVisible: true, value: '' })}
              className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
              title="Create new folder"
            >
              <Plus className="w-3 h-3" />
              New Folder
            </button>
          )}
        </div>
      </div>

      {/* Tree Content */}
      <div className="p-2 max-h-96 overflow-y-auto">
        {/* Root folder */}
        <div
          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
            currentPath === '' ? 'bg-blue-900 text-blue-300' : 'hover:bg-gray-700 text-gray-300'
          }`}
          onClick={handleRootSelect}
        >
          <Folder className="w-4 h-4" />
          <span className="text-sm">Matter</span>
        </div>

        {/* Folder tree */}
        {treeItems.map((folder) => (
          <div key={folder.id}>
            {/* Folder item */}
            <div
              className={`flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                currentPath === folder.path ? 'bg-blue-900 text-blue-300' : 'hover:bg-gray-700 text-gray-300'
              }`}
              style={{ paddingLeft: `${folder.level * 12 + 8}px` }}
              onClick={() => handleFolderSelect(folder)}
              onContextMenu={(e) => handleContextMenu(e, folder)}
            >
              {/* Expand/collapse button */}
              {folder.hasChildren && (
                <button
                  onClick={(e) => handleToggleExpand(folder, e)}
                  className="p-0.5 hover:bg-gray-600 rounded"
                >
                  {folder.isExpanded ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
              )}
              {!folder.hasChildren && <div className="w-4" />}

              {/* Folder icon */}
              {folder.isExpanded ? (
                <FolderOpen className="w-4 h-4 text-blue-400" />
              ) : (
                <Folder className="w-4 h-4 text-blue-400" />
              )}

              {/* Folder name */}
              <span className="text-sm truncate flex-1">{folder.name}</span>

              {/* More options */}
              <button
                onClick={(e) => handleContextMenu(e, folder)}
                className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-gray-600 rounded"
              >
                <MoreVertical className="w-3 h-3" />
              </button>
            </div>

            {/* New folder input */}
            {newFolderInput.isVisible && newFolderInput.parentPath === folder.path && (
              <div 
                className="bg-gray-750 border border-gray-600 rounded-lg mx-2 mb-2"
                style={{ marginLeft: `${folder.level * 12 + 8}px` }}
              >
                <div className="flex items-center gap-2 p-2 pb-1">
                  <Folder className="w-4 h-4 text-blue-400" />
                  <input
                    type="text"
                    value={newFolderInput.value}
                    onChange={(e) => setNewFolderInput(prev => ({ ...prev, value: e.target.value }))}
                    onKeyDown={handleCreateNewFolder}
                    placeholder="Folder name..."
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    autoFocus
                  />
                </div>
                <div className="flex items-center gap-2 p-2 pt-1">
                  <button
                    onClick={async () => {
                      console.log('🔵 Nested Create Folder button clicked!');
                      console.log('Current input value:', newFolderInput.value);
                      console.log('Parent path for subfolder:', newFolderInput.parentPath);
                      console.log('Matter ID:', matter.id);
                      
                      if (newFolderInput.value.trim()) {
                        const folderName = newFolderInput.value.trim();
                        const parentPath = newFolderInput.parentPath;
                        
                        // Hide form immediately when Create button is clicked
                        setNewFolderInput({ parentPath: '', isVisible: false, value: '' });
                        setContextMenu(null);
                        
                        try {
                          console.log('📁 Starting nested folder creation...');
                          console.log(`🔍 Creating folder "${folderName}" with parent path: "${parentPath}"`);
                          const result = await createFolder(matter, folderName, parentPath);
                          console.log('✅ Nested folder creation successful:', result);
                          
                          // Refresh folder list to show new folder
                          console.log('🔄 Refreshing folder list...');
                          await loadFolders(matter, ''); // Refresh entire tree
                          
                          if (onFolderCreate) {
                            console.log('📞 Calling onFolderCreate callback');
                            onFolderCreate(folderName, parentPath);
                          }
                          
                          // Clear all UI states after successful creation
                          setExpandedFolders(prev => new Set([...prev, parentPath]));
                          console.log('🎉 Nested folder creation process completed');
                        } catch (error) {
                          console.error('❌ Failed to create nested folder:', error);
                          alert(`Failed to create folder: ${error instanceof Error ? error.message : 'Unknown error'}`);
                        }
                      } else {
                        console.log('⚠️ Empty folder name, not creating nested folder');
                      }
                    }}
                    disabled={!newFolderInput.value.trim()}
                    className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                  >
                    Create Folder
                  </button>
                  <button
                    onClick={() => setNewFolderInput({ parentPath: '', isVisible: false, value: '' })}
                    className="px-3 py-1.5 bg-gray-600 text-gray-300 rounded text-sm hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                <div className="text-xs text-gray-400 px-2 pb-2">
                  Press Enter to save, Escape to cancel
                </div>
              </div>
            )}
          </div>
        ))}

        {/* New root folder input */}
        {newFolderInput.isVisible && newFolderInput.parentPath === '' && (
          <div className="px-2 py-2 bg-gray-750 border border-gray-600 rounded-lg mx-2 mb-2">
            <div className="flex items-center gap-2 mb-2">
              <Folder className="w-4 h-4 text-blue-400" />
              <input
                type="text"
                value={newFolderInput.value}
                onChange={(e) => setNewFolderInput(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={handleCreateNewFolder}
                placeholder="Folder name..."
                className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  console.log('🔵 Create Folder button clicked!');
                  console.log('Current input value:', newFolderInput.value);
                  console.log('Parent path:', newFolderInput.parentPath);
                  console.log('Matter:', matter);
                  
                  if (newFolderInput.value.trim()) {
                    const folderName = newFolderInput.value.trim();
                    const parentPath = newFolderInput.parentPath;
                    
                    // Hide form immediately when Create button is clicked
                    setNewFolderInput({ parentPath: '', isVisible: false, value: '' });
                    setContextMenu(null);
                    
                    try {
                      console.log('📁 Starting folder creation...');
                      console.log('Calling createFolder with:', { matter: matter.id, folderName, parentPath });
                      const result = await createFolder(matter, folderName, parentPath);
                      console.log('✅ Folder creation successful:', result);
                      
                      // Refresh folder list to show new folder
                      console.log('🔄 Refreshing folder list...');
                      await loadFolders(matter, ''); // Refresh entire tree
                      
                      if (onFolderCreate) {
                        console.log('📞 Calling onFolderCreate callback');
                        onFolderCreate(folderName, parentPath);
                      }
                      
                      // Clear all UI states after successful creation
                      setExpandedFolders(prev => new Set([...prev, parentPath]));
                      console.log('🎉 Folder creation process completed');
                    } catch (error) {
                      console.error('❌ Failed to create folder:', error);
                      // Show error to user
                      alert(`Failed to create folder: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    }
                  } else {
                    console.log('⚠️ Empty folder name, not creating');
                  }
                }}
                disabled={!newFolderInput.value.trim()}
                className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                Create Folder
              </button>
              <button
                onClick={() => setNewFolderInput({ parentPath: '', isVisible: false, value: '' })}
                className="px-3 py-1.5 bg-gray-600 text-gray-300 rounded text-sm hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Press Enter to save, Escape to cancel
            </div>
          </div>
        )}

        {/* Loading state */}
        {isFolderLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Empty state */}
        {!isFolderLoading && treeItems.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <Folder className="w-6 h-6 mx-auto mb-2" />
            <p className="text-sm">No folders created yet</p>
            <p className="text-xs mt-1">Click "New Folder" above to get started</p>
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <FolderContextMenu
          position={contextMenu.position}
          onClose={closeContextMenu}
          onCreateFolder={handleCreateFolderFromContext}
          onDeleteFolder={handleDeleteFolderFromContext}
        />
      )}
    </div>
  );
};

export default DocumentTree;
